import type { TFunction } from 'i18next';

export class PostRequestError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'PostRequestError';
  }
}

/** Read the API's structured error without exposing an HTML proxy error page. */
export async function readPostError(response: Response, fallback: string): Promise<PostRequestError> {
  const body: unknown = await response.json().catch(() => null);
  if (body && typeof body === 'object') {
    const data = body as { error?: string | { message?: unknown; code?: unknown }; message?: unknown; code?: unknown };
    const message = typeof data.error === 'string' ? data.error : data.error?.message ?? data.message;
    const errorCode = typeof data.error === 'object' ? data.error?.code ?? data.code : data.code;
    const code = typeof errorCode === 'string' ? errorCode : undefined;
    if (typeof message === 'string' && message.trim() && !/<(?:!doctype|html|body)\b/i.test(message)) {
      return new PostRequestError(message, response.status, code);
    }
    return new PostRequestError(`${fallback} (HTTP ${response.status})`, response.status, code);
  }
  return new PostRequestError(`${fallback} (HTTP ${response.status})`, response.status);
}

export function getPostDeleteErrorMessage(error: unknown, t: TFunction): string {
  const message = error instanceof Error ? error.message : '';
  if (/failed to fetch|fetch failed|network ?error|network request failed|load failed/i.test(message)) {
    return t('postDeleteErrors.network', 'The server response could not be received. Refresh My Content to check whether the post was deleted before trying again.');
  }
  return message || t('postDeleteErrors.unknown', 'Unable to delete the post. Please try again later.');
}

export function getPostSaveErrorMessage(error: unknown, t: TFunction): string {
  const message = error instanceof Error ? error.message : '';
  if (/failed to fetch|fetch failed|network ?error|network request failed|load failed/i.test(message)) {
    return t('postSaveErrors.network', 'The server response could not be received. Your edits are still in this form. Check your connection and check My Content in another tab before trying again to avoid creating a duplicate.');
  }
  if ((error instanceof PostRequestError && error.status >= 500) || /<(?:!doctype|html|body)\b/i.test(message)) {
    return t('postSaveErrors.server', 'The server is temporarily unavailable. Your edits are still in this form. Please try again later.');
  }
  return message || t('postSaveErrors.unknown', 'Unable to save the post. Your edits are still in this form.');
}
